/**
 * Which circles an entry is in, as chips.
 *
 * A chip per circle, in tree order and indented by depth, because an entry can
 * be in several at once (docs/CIRCLES.md §1) and a `<select multiple>` hides
 * that behind a scrollbar. Picking is a toggle; a circle you find you need
 * halfway through adding someone is made from the last chip in the row rather
 * than from a trip to another panel.
 *
 * The chips carry no opinion about *when* the set is saved: the event form
 * writes them with the entry, and the detail panel uses the same component
 * read-only as a way to point at a ring.
 */
import type { Tag } from '@circle/shared';
import { buildCircleTree, flattenTree } from './circles';
import { el, setChildren } from './dom';

export interface TagChipsOptions {
  /** Read live, so a circle made from the row shows up without a rebuild. */
  tags: () => Tag[];
  selected?: readonly string[];
  /** False for the detail panel, where a chip points at a ring instead. */
  editable?: boolean;
  onChange?: (tagIds: string[]) => void;
  /** Offer "+ New circle". Absent: no way to make one from here. */
  onCreate?: (name: string) => Promise<Tag | undefined>;
  /** A chip was clicked while not editable. */
  onPick?: (tagId: string) => void;
  /** Shown when the calendar has no circles at all. */
  emptyText?: string;
}

export class TagChips {
  readonly root: HTMLElement;
  private readonly options: TagChipsOptions;
  private selected: Set<string>;
  private making = false;

  constructor(options: TagChipsOptions) {
    this.options = options;
    this.selected = new Set(options.selected ?? []);
    this.root = el('div', { class: 'chips', role: 'group', 'aria-label': 'Circles' });
    this.paint();
  }

  /** The chosen circles, in the tree's order rather than the clicking's. */
  get value(): string[] {
    return this.ordered().filter((tag) => this.selected.has(tag.id)).map((tag) => tag.id);
  }

  set value(ids: readonly string[]) {
    this.selected = new Set(ids);
    this.paint();
  }

  paint(): void {
    const tags = this.ordered();
    const editable = this.options.editable !== false;
    if (tags.length === 0 && !this.options.onCreate) {
      setChildren(this.root, [
        el('span', {
          class: 'small muted',
          text: this.options.emptyText ?? 'No circles yet.',
        }),
      ]);
      return;
    }
    setChildren(this.root, [
      ...tags.map((tag) => this.chip(tag, editable)),
      editable && this.options.onCreate ? this.newChip() : null,
    ]);
  }

  private ordered(): (Tag & { depth: number })[] {
    const tree = buildCircleTree(this.options.tags());
    return flattenTree(tree)
      .filter((node) => node.tag)
      .map((node) => ({ ...node.tag!, depth: node.depth }));
  }

  private chip(tag: Tag & { depth: number }, editable: boolean): HTMLElement {
    const on = this.selected.has(tag.id);
    const chip = el(
      'button',
      {
        class: 'chip',
        type: 'button',
        'data-tag': tag.id,
        'data-depth': String(tag.depth),
        'aria-pressed': on ? 'true' : 'false',
        ...(tag.colour ? { style: `--chip:${tag.colour}` } : {}),
      },
      el('span', { class: 'chip__dot', 'aria-hidden': 'true' }),
      el('span', { text: tag.name }),
    );
    chip.addEventListener('click', () => {
      if (!editable) {
        this.options.onPick?.(tag.id);
        return;
      }
      if (on) this.selected.delete(tag.id);
      else this.selected.add(tag.id);
      this.paint();
      this.options.onChange?.(this.value);
    });
    return chip;
  }

  /** The last chip in the row: a name goes in, a circle comes out selected. */
  private newChip(): HTMLElement {
    if (!this.making) {
      return el('button', {
        class: 'chip chip--new',
        type: 'button',
        'data-role': 'new-circle',
        text: '+ New circle',
        onClick: () => {
          this.making = true;
          this.paint();
          this.root.querySelector<HTMLInputElement>('.chip__input')?.focus();
        },
      });
    }

    const input = el('input', {
      class: 'chip__input',
      placeholder: 'Family',
      maxlength: '60',
      'aria-label': 'Name of the new circle',
    });
    const cancel = (): void => {
      this.making = false;
      this.paint();
    };
    const commit = (): void => {
      const name = input.value.trim();
      if (!name) return cancel();
      input.disabled = true;
      void this.options
        .onCreate?.(name)
        .then((created) => {
          if (created) {
            this.selected.add(created.id);
            this.options.onChange?.(this.value);
          }
        })
        .finally(cancel);
    };
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commit();
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        cancel();
      }
    });
    input.addEventListener('blur', commit);
    return input;
  }
}
